import { useMemo } from 'react'

import { filterNil } from 'src/utils'

import {
  CreateCompanyMutation,
  CreateCompanyMutationVariables,
  GetCompanyByIdQuery,
  GetCompanyByIdQueryVariables,
  GetCompanyByUuidQuery,
  GetCompanyByUuidQueryVariables,
  GetMyCompaniesQuery,
  GetMyCompaniesQueryVariables,
  InviteUserToCompanyMutation,
  InviteUserToCompanyMutationVariables,
  RejectCompanyInvitationMutationVariables,
  RejectCompanyInvitationMutation,
  RequestToJoinCompanyMutation,
  RequestToJoinCompanyMutationVariables,
  SearchCompaniesQuery,
  SearchCompaniesQueryVariables,
  UpdateCompanyMutation,
  UpdateCompanyMutationVariables,
  UpdateUserOnCompanyMutation,
  UpdateUserOnCompanyMutationVariables,
  RemoveUserFromCompanyMutation,
  RemoveUserFromCompanyMutationVariables,
  AcceptCompanyInvitationMutation,
  AcceptCompanyInvitationMutationVariables,
  RejectRequestToJoinCompanyMutation,
  RejectRequestToJoinCompanyMutationVariables,
  AcceptRequestToJoinCompanyMutation,
  AcceptRequestToJoinCompanyMutationVariables,
  MergeCompaniesMutation,
  MergeCompaniesMutationVariables,
  GetCompaniesByUserQuery,
  GetCompaniesByUserQueryVariables,
} from '../../types/graphql'
import { QueryKey, useMutation, useQuery } from '../utils/queryClient'

const Update = gql`
  mutation UpdateCompanyMutation($id: Int!, $input: UpdateCompanyInput!) {
    updateCompany(id: $id, input: $input) {
      ...CompanyFragment
    }
  }
`
export const useUpdateCompanyMutation = () => {
  return useMutation<UpdateCompanyMutation, UpdateCompanyMutationVariables>({
    mutationDocument: Update,
  })
}

const Create = gql`
  mutation CreateCompanyMutation($input: CreateCompanyInput!) {
    createCompany(input: $input) {
      ...CompanyFragment
    }
  }
`
export const useCreateCompanyMutation = () => {
  return useMutation<CreateCompanyMutation, CreateCompanyMutationVariables>({
    mutationDocument: Create,
  })
}

const GetMyCompanies = gql`
  query GetMyCompaniesQuery {
    me {
      activeInvitationsToJoinCompanies {
        ...UserCompanyInvitationFragment
      }
      companies {
        ...CompanyFragment
      }
      pendingRequestsToJoinCompanies {
        ...UserRequestToJoinCompanyFragment
      }
    }
  }
`
export const useMyCompaniesQuery = () => {
  const { data, ...rest } = useQuery<GetMyCompaniesQuery, GetMyCompaniesQueryVariables>({
    queryKey: [QueryKey.MyCompanies],
    queryDocument: GetMyCompanies,
    variables: {},
  })
  const { activeInvitationsToJoinCompanies, companies, pendingRequestsToJoinCompanies } =
    useMemo(() => {
      if (!data?.me) {
        return {
          activeInvitationsToJoinCompanies: [],
          companies: [],
          pendingRequestsToJoinCompanies: [],
        }
      }
      return {
        activeInvitationsToJoinCompanies: data.me?.activeInvitationsToJoinCompanies ?? [],
        companies: data.me.companies ?? [],
        pendingRequestsToJoinCompanies: data.me.pendingRequestsToJoinCompanies ?? [],
      }
    }, [data])
  return {
    ...rest,
    activeInvitationsToJoinCompanies,
    companies,
    pendingRequestsToJoinCompanies,
  }
}

export const GetCompaniesByUser = gql`
  query GetCompaniesByUserQuery($userUuid: String!) {
    userByUuid(uuid: $userUuid) {
      companies {
        ...CompanyWithRoleFragment
      }
    }
  }
  fragment CompanyWithRoleFragment on Company {
    ...CompanyFragment
    userRole(userUuid: $userUuid)
  }
`
export const useCompaniesByUserQuery = (userUuid: string) => {
  const { data, ...rest } = useQuery<GetCompaniesByUserQuery, GetCompaniesByUserQueryVariables>({
    queryKey: [QueryKey.CompaniesByUser, userUuid],
    queryDocument: GetCompaniesByUser,
    variables: { userUuid },
  })
  const companies = useMemo(() => data?.userByUuid?.companies ?? [], [data])
  return {
    companies,
    ...rest,
  }
}

export const SearchCompanies = gql`
  query SearchCompaniesQuery($input: SearchCompaniesInput!) {
    searchCompanies(input: $input) {
      ...CompanySearchResultFragment
    }
  }
  fragment CompanySearchResultFragment on Company {
    ...CompanyFragment
    createdByUser {
      ...UserDataFragment
    }
  }
`
export const useSearchCompaniesQuery = (searchQuery: string) => {
  const { data, ...rest } = useQuery<SearchCompaniesQuery, SearchCompaniesQueryVariables>({
    queryKey: [QueryKey.SearchCompanies, searchQuery],
    queryDocument: SearchCompanies,
    variables: {
      input: {
        query: searchQuery,
      },
    },
    enabled: !!searchQuery,
  })
  const companies = useMemo(() => {
    return data?.searchCompanies ?? []
  }, [data])
  return {
    ...rest,
    companies,
  }
}

const RequestToJoinCompany = gql`
  mutation RequestToJoinCompanyMutation($idPair: UserCompanyIdPair!) {
    requestToJoinCompany(idPair: $idPair) {
      ...CompanyFragment
    }
  }
`
export const useRequestToJoinCompanyMutation = () => {
  return useMutation<RequestToJoinCompanyMutation, RequestToJoinCompanyMutationVariables>({
    mutationDocument: RequestToJoinCompany,
  })
}

const AcceptRequestToJoinCompany = gql`
  mutation AcceptRequestToJoinCompanyMutation(
    $idPair: UserCompanyIdPair!
    $input: UserOnCompanyInput!
  ) {
    acceptUserRequestToJoinCompany(idPair: $idPair, input: $input) {
      ...CompanyFragment
    }
  }
`
export const useAcceptRequestToJoinCompanyMutation = () => {
  return useMutation<
    AcceptRequestToJoinCompanyMutation,
    AcceptRequestToJoinCompanyMutationVariables
  >({
    mutationDocument: AcceptRequestToJoinCompany,
  })
}

const RejectRequestToJoinCompany = gql`
  mutation RejectRequestToJoinCompanyMutation($idPair: UserCompanyIdPair!) {
    rejectUserRequestToJoinCompany(idPair: $idPair) {
      ...CompanyFragment
    }
  }
`
export const useRejectRequestToJoinCompanyMutation = () => {
  return useMutation<
    RejectRequestToJoinCompanyMutation,
    RejectRequestToJoinCompanyMutationVariables
  >({
    mutationDocument: RejectRequestToJoinCompany,
  })
}

const GetCompanyById = gql`
  query GetCompanyByIdQuery($id: Int!) {
    companyById(id: $id) {
      ...CompanyFragment
      activeInvitations {
        ...UserCompanyInvitationFragment
      }
      pendingRequests {
        ...UserRequestToJoinCompanyFragment
      }
      usersOnCompany {
        ...UserOnCompanyFragment
      }
    }
  }
`
export const useCompanyByIdQuery = (id: number | null | undefined) => {
  const { data, ...rest } = useQuery<GetCompanyByIdQuery, GetCompanyByIdQueryVariables>({
    queryKey: [QueryKey.Company, id],
    queryDocument: GetCompanyById,
    variables: {
      id: id ?? -1,
    },
    enabled: !!id,
  })
  const company = useMemo(() => {
    return data?.companyById ?? null
  }, [data])
  const activeInvitations = useMemo(() => {
    return filterNil(company?.activeInvitations ?? [])
  }, [company?.activeInvitations])
  const pendingRequests = useMemo(() => {
    return filterNil(data?.companyById?.pendingRequests ?? [])
  }, [data?.companyById?.pendingRequests])
  const usersOnCompany = useMemo(() => {
    return filterNil(company?.usersOnCompany ?? [])
  }, [company?.usersOnCompany])
  const activeInvitationsByUserId = useMemo(
    () => new Set(activeInvitations.map((ui) => ui.user?.id)),
    [activeInvitations]
  )
  const pendingRequestsByUserId = useMemo(
    () => new Set(pendingRequests.map((ur) => ur.user?.id)),
    [pendingRequests]
  )
  const usersOnCompanyByUserId = useMemo(
    () => new Set(usersOnCompany.map((uoc) => uoc.user?.id)),
    [usersOnCompany]
  )

  return {
    ...rest,
    activeInvitations,
    activeInvitationsByUserId,
    pendingRequests,
    pendingRequestsByUserId,
    company,
    usersOnCompany,
    usersOnCompanyByUserId,
  }
}

const GetCompanyByUuid = gql`
  query GetCompanyByUuidQuery($uuid: String!) {
    companyByUuid(uuid: $uuid) {
      ...CompanyFragment
      activeInvitations {
        ...UserCompanyInvitationFragment
      }
      pendingRequests {
        ...UserRequestToJoinCompanyFragment
      }
      usersOnCompany {
        ...UserOnCompanyFragment
      }
    }
  }
`
export const useCompanyByUuidQuery = (uuid: string | null | undefined) => {
  const { data, ...rest } = useQuery<GetCompanyByUuidQuery, GetCompanyByUuidQueryVariables>({
    queryKey: [QueryKey.Company, uuid],
    queryDocument: GetCompanyByUuid,
    variables: {
      uuid: uuid ?? '',
    },
    enabled: !!uuid,
  })
  const company = useMemo(() => {
    return data?.companyByUuid ?? null
  }, [data])
  const activeInvitations = useMemo(() => {
    return filterNil(company?.activeInvitations ?? [])
  }, [company?.activeInvitations])
  const pendingRequests = useMemo(() => {
    return filterNil(company?.pendingRequests ?? [])
  }, [company?.pendingRequests])
  const usersOnCompany = useMemo(() => {
    return filterNil(company?.usersOnCompany ?? [])
  }, [company?.usersOnCompany])
  return {
    ...rest,
    activeInvitations,
    company,
    pendingRequests,
    usersOnCompany,
  }
}

const InviteUserToCompany = gql`
  mutation InviteUserToCompanyMutation($idPair: UserCompanyIdPair!, $input: UserOnCompanyInput!) {
    inviteUserToCompany(idPair: $idPair, input: $input) {
      ...CompanyFragment
    }
  }
`
export const useInviteUserToCompanyMutation = () => {
  return useMutation<InviteUserToCompanyMutation, InviteUserToCompanyMutationVariables>({
    mutationDocument: InviteUserToCompany,
  })
}

const AcceptCompanyInvitation = gql`
  mutation AcceptCompanyInvitationMutation($idPair: UserCompanyIdPair!) {
    acceptCompanyInvitation(idPair: $idPair) {
      ...CompanyFragment
    }
  }
`
export const useAcceptCompanyInvitationMutation = () => {
  return useMutation<AcceptCompanyInvitationMutation, AcceptCompanyInvitationMutationVariables>({
    mutationDocument: AcceptCompanyInvitation,
  })
}

const RejectCompanyInvitation = gql`
  mutation RejectCompanyInvitationMutation($idPair: UserCompanyIdPair!) {
    rejectCompanyInvitation(idPair: $idPair) {
      id
    }
  }
`
export const useRejectCompanyInvitationMutation = () => {
  return useMutation<RejectCompanyInvitationMutation, RejectCompanyInvitationMutationVariables>({
    mutationDocument: RejectCompanyInvitation,
  })
}

const UpdateUserOnCompany = gql`
  mutation UpdateUserOnCompanyMutation($idPair: UserCompanyIdPair!, $input: UserOnCompanyInput!) {
    updateUserOnCompany(idPair: $idPair, input: $input) {
      ...CompanyFragment
    }
  }
`
export const useUpdateUserOnCompanyMutation = () => {
  return useMutation<UpdateUserOnCompanyMutation, UpdateUserOnCompanyMutationVariables>({
    mutationDocument: UpdateUserOnCompany,
  })
}

const RemoveUserFromCompany = gql`
  mutation RemoveUserFromCompanyMutation($idPair: UserCompanyIdPair!) {
    removeUserFromCompany(idPair: $idPair) {
      ...CompanyFragment
    }
  }
`
export const useRemoveUserFromCompanyMutation = () => {
  return useMutation<RemoveUserFromCompanyMutation, RemoveUserFromCompanyMutationVariables>({
    mutationDocument: RemoveUserFromCompany,
  })
}

const MergeCompanies = gql`
  mutation MergeCompaniesMutation($input: MergeCompaniesInput!) {
    mergeCompanies(input: $input) {
      ...CompanyFragment
    }
  }
`
export const useMergeCompaniesMutation = () => {
  return useMutation<MergeCompaniesMutation, MergeCompaniesMutationVariables>({
    mutationDocument: MergeCompanies,
  })
}
